/**
 * React Query hook for Case Matters (Global)
 * Provides automatic caching, background sync, and error handling
 * Can be used across all modules
 */

import { useQuery } from '@tanstack/react-query';
import { getCaseMatter } from 'services/lead/leadServices';
import { totalCountEvent } from 'services/generalServices';
import { queryKeys } from '../config/queryClient';

/**
 * Fetches case matters data
 * Always loads the full list by getting total count first, then fetching all items
 *
 * @param {Object} params - Query parameters
 * @param {number} params.pageLimit - Limit for fetching matters
 * @param {string} params.pagination_token - Pagination token (default: '')
 * @param {string} params.pagination_direction - Pagination direction (default: 'forward')
 * @param {boolean} params.excludeFromLoading - Exclude from loading state (default: false)
 * @param {string|Array} params.practiceAreaId - Practice area filter (default: '')
 * @returns {Promise<Array>} - Array of case matters
 */
const fetchCaseMatters = async ({
  pageLimit,
  pagination_token = '',
  pagination_direction = 'forward',
  excludeFromLoading = false,
  practiceAreaId = '',
}) => {
  try {
    const response = await getCaseMatter({
      pageLimit,
      pagination_token,
      pagination_direction,
      excludeFromLoading,
      practiceAreaId,
    });

    return response?.data?.['case-matter'] || [];
  } catch (error) {
    console.error('Error fetching case matters:', error);
    throw error;
  }
};

/**
 * Hook to fetch case matters with React Query
 * Always loads the full list by getting total count first (same as useCaseMatter)
 *
 * @param {Object} options - Configuration options
 * @param {number} options.pageLimit - Limit for fetching matters (default: uses total count)
 * @param {string} options.pagination_token - Pagination token (default: '')
 * @param {string} options.pagination_direction - Pagination direction (default: 'forward')
 * @param {boolean} options.excludeFromLoading - Exclude from loading state (default: false)
 * @param {string|Array} options.practiceAreaId - Practice area filter (default: '')
 * @param {boolean} options.enabled - Enable/disable query (default: true)
 * @param {boolean} options.loadFullList - If true, gets total count first (default: true)
 *
 * @returns {Object} - caseMatterList, isCaseMatterLoading, error, refetch, isFetching
 */
export const useCaseMattersQuery = ({
  pageLimit,
  pagination_token = '',
  pagination_direction = 'forward',
  excludeFromLoading = false,
  practiceAreaId = '',
  enabled = true,
  loadFullList = true,
} = {}) => {
  // Get total count first (same as useCaseMatter) - only if loadFullList is true and pageLimit is not provided
  // This count is shared across ALL reports to ensure consistent cache keys
  const { data: countData, isLoading: isCountLoading } = useQuery({
    queryKey: ['case-matter-count'],
    queryFn: async () => {
      const countResponse = await totalCountEvent('case-matter');
      return countResponse?.data || 10;
    },
    enabled: enabled && loadFullList && !pageLimit,
    staleTime: 30 * 60 * 1000, // Increased to 30 minutes - count rarely changes
    gcTime: 60 * 60 * 1000, // Keep in cache for 1 hour
    refetchOnWindowFocus: false,
    refetchOnMount: false, // Don't refetch when component mounts - use cache
    retry: 2,
  });

  // Determine the limit value
  // For loadFullList mode, wait for countData before proceeding
  const limit = pageLimit || (loadFullList ? countData : 10) || 10;

  // For shared caching across reports, we need to ensure all reports use the same cache key
  // Only enable the query when limit is stable (countData is loaded for loadFullList mode)
  const isLimitStable = !!pageLimit || !loadFullList || !!countData;

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: queryKeys.caseMatters.list(limit, pagination_token, pagination_direction, practiceAreaId),
    queryFn: () =>
      fetchCaseMatters({
        pageLimit: limit,
        pagination_token,
        pagination_direction,
        excludeFromLoading,
        practiceAreaId,
      }),
    // Only fetch when enabled AND limit is stable to prevent cache key changes
    enabled: enabled && isLimitStable,
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
    // refetchOnMount is intentionally not set to false, allowing refetch of invalidated (stale) data
    // This ensures that when data is updated in Settings, reports see the changes immediately
    retry: 2,
  });

  return {
    caseMatterList: data || [],
    isCaseMatterLoading: isLoading || (loadFullList && isCountLoading), // Include count loading state
    isFetching,
    error,
    refetch,
  };
};
